//=====================================================
//　G003：予約日付選択
//=====================================================
import { createContext, useEffect, useState, useContext, useLayoutEffect } from "react";
import { AxiosResponse } from "axios";
import { getApiData } from "../../utils/CallApi";
import { useHistory, useParams } from "react-router-dom";
import { ContentsBox } from "../../common/ContentsBox";
import { ContentsTitle } from "../../common/ContentsTitle";
import { G003CalendarNotes } from "./G003CalendarNotes";
import { G003CalendarMonthly } from "./G003CalendarMonthly";
import { G003CalendarWeekly } from "./G003CalendarWeekly";
import { BtnReturn } from "../../common/BtnReturn";
import { ReservationFlow, ProcessName } from "../../common/ReservationFlow";
import { ReservationInfo } from "../../common/ReservationInfo";
import { CtxClinicInfo, CtxClinicSystemInfo, CtxNewReservation, CtxSystemErr } from "../../utils/Contexts";
import { useAuth } from "../../utils/UseAuth";
import { ReservationType } from "../../utils/ConstCommon";
import { ConstMsg } from "../../utils/ConstMsg";
import { MsgBoxErr } from "../../common/MsgBoxErr";
import { LoadingBox } from "../../common/LoadingBox";

interface IFCtxCalendarInfo {
    cDate: Date,
    setCDate: React.Dispatch<React.SetStateAction<Date>>,
    cMonth: Date,
    setCMonth: React.Dispatch<React.SetStateAction<Date>>,
    moveflg:boolean,
    setMoveflg: React.Dispatch<React.SetStateAction<boolean>>,
}

export interface IFG003Param {
    mode: string
}

//カレンダーモード（月/週）
export const G003MODE_MONTHLY = "monthly";
export const G003MODE_WEEKLY = "weekly";

//コンテキスト宣言
export const CtxG003 = createContext<IFCtxCalendarInfo>({cDate: new Date(), setCDate: function(){}, cMonth: new Date(), setCMonth: function(){}, moveflg:false, setMoveflg: function(){}});

export function G003(){

    const params = useParams<IFG003Param>();
    const history = useHistory();
    const cci = useContext(CtxClinicInfo);
    const cnr = useContext(CtxNewReservation);
    const ccsi = useContext(CtxClinicSystemInfo);
    const ua = useAuth();
    const cserr = useContext(CtxSystemErr);

    // ブラウザバックで表示させない処理
    useEffect(function(){
        //タイトル追加処理
        if(cci.data.name){
            document.title = cci.data.name + " | Web予約 - 予約日付選択";
        }else{
            document.title = process.env.REACT_APP_DEFAULT_CLININC_NAME + " | Web予約 - 予約日付選択";
        }

        // マウントフラグ
        let isMounted = true;
        
        // アンマウント時はstate(history)を更新しないようにする
        if(isMounted){
            // 完了データの場合、マイページ、トップ画面へ遷移
            if(cnr.data.done){
                if(ua.isLoggedIn()){
                    history.push('/mypage');
                } else {
                    history.push('/');
                }
            }
            setMoveflg(false);
        }

        // cleanup関数(Unmount時の処理)
        return () => {
            // マウントオフ
            isMounted = false;
        }
        // アンマウント時にのみ一回だけ実行したいため第2引数指定なし。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(function(){
        // マウントフラグ
        let isMounted = true;
        
        // 12ヵ月分まで取得する
        let callLimit = 12;

        if (cnr.data.medicalTypeId && cnr.data.date === undefined) {
            // 最早予約可能日取得処理
            let getCalendarMonthly = function (cDate: Date) {

                const year = cDate.getFullYear();
                const month = cDate.getMonth() + 1;
                const reserveId = cnr.data.reserveId;
                const patientId = (cnr.type === ReservationType.First ? "" : ua.getPatientNo());

                let apiUrl = `/c/${cci.data.id}/tables/${cnr.data.medicalTypeId}/${year}/${month}`;
                let apiParams = {
                    params: {
                        reserveId: reserveId,
                        patientId: patientId
                    }
                };

                getApiData(
                    apiUrl,
                    apiParams,
                    function (res: AxiosResponse<any>) {
                        callbackFunc(res);
                    }
                );
            };

            let callbackFunc = function (res: any) {
                // アンマウント時はstateを更新しないようにする
                if (isMounted) {
                    if (res?.status) {
                        if (res.status === 200) {
                            // 取得した月の中で予約可能な日付のうち最早である日を取得
                            const firstAvailableDate = (res.data.frames).find((i: any) => (i.statusAm === 1 || i.statusAm === 2 || i.statusPm === 1 || i.statusPm === 2))
                            if (firstAvailableDate) {
                                // 予約可能な日付が存在する場合
                                const fad = new Date(res.data.month + "/" + firstAvailableDate.day);
                                if (cci.data.showWeeklyFirst) {
                                    // 週表示の場合は最早の日付を左端にする
                                    fad.setDate(fad.getDate() + 3);
                                }
                                setCDate(fad);
                                setCMonth(fad);
                                if (cci.data.showWeeklyFirst) {
                                    setMoveflg(true);
                                    history.replace("/reservation/date/weekly");
                                }
                                setErrmess("");
                                setLoadingDone(true);
                            } else {
                                // 予約可能な日付が存在しない場合
                                const targetMonth = new Date(res.data.month + "/01");
                                targetMonth.setMonth(targetMonth.getMonth() + 1);
                                callLimit -= 1;
                                if (callLimit <= 0 || targetMonth > new Date(res.data.period)) {
                                    // 12か月分見たか予約可能最終日まで見た場合
                                    if (cci.data.showWeeklyFirst) {
                                        setMoveflg(true);
                                        history.replace("/reservation/date/weekly");
                                    }
                                    setLoadingDone(true);
                                } else {
                                    // 次の月を取得
                                    getCalendarMonthly(targetMonth);
                                }
                            }
                        } else if (res.status === 400) {
                            setErrmess(res.data.message);
                            setLoadingDone(true);
                        } else if (res.status === 403 || res.status === 404 || res.status >= 500) {
                            cserr.setErrmess(res.data.message);
                            setLoadingDone(true);
                        } else {
                            cserr.setErrmess(ConstMsg.EG0030001);
                        }
                    } else {
                        cserr.setErrmess(ConstMsg.EG0030001);
                    }
                }
            };

            getCalendarMonthly(new Date());
        }else{
            if(isMounted){
                setLoadingDone(true);
            }
        }

        // cleanup関数(Unmount時の処理)
        return () => {
            // マウントオフ
            isMounted = false;
        }        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]
    );

    //現在の月
    const [cDate, setCDate] = useState<Date>(new Date());
    //月カレンダーで表示していた月
    const [cMonth, setCMonth] = useState<Date>(new Date());
    //カレンダー表示月移動時のフラグ
    const [moveflg, setMoveflg] = useState(false);

    const [errmess, setErrmess] = useState("");
    const [loadingDone, setLoadingDone] = useState(false);  //読込中フラグ

    //モード切り替えのクラス
    let modeClass = "calendar-monthly";
    if (params.mode === G003MODE_WEEKLY){
        modeClass = "calendar-weekly";
    }

    //ReservationInfo表示フラグ
    //(再診かつ治療種別表示フラグOFFかつ担当医表示フラグOFFかつ予約日時未選択)の場合表示しない。それ以外の場合表示する。
    const isDisplayReservationInfo = !((cnr.type === ReservationType.Return || cnr.type === ReservationType.Change)
        && !ccsi.data.display.medicalType && !ccsi.data.display.doctor && !cnr.data.date && !cnr.data.beforeDate);

    return (
        <>
            <ReservationFlow currentProcess={ProcessName.Date}/>
            {isDisplayReservationInfo &&
                <ContentsBox>
                    <ReservationInfo />
                </ContentsBox>
            }
            <ContentsBox>
                {errmess && <MsgBoxErr text={errmess} />}
                {loadingDone ?
                    <CtxG003.Provider value={{ cDate: cDate, setCDate: setCDate, cMonth: cMonth, setCMonth: setCMonth, moveflg: moveflg, setMoveflg: setMoveflg }}>
                        <ContentsTitle title="ご希望の予約日時を選択してください" />
                        <div className={modeClass}>
                            <G003CalendarNotes />
                            {params.mode === G003MODE_MONTHLY ?
                                <G003CalendarMonthly />
                                :
                                <G003CalendarWeekly />
                            }
                        </div>
                    </CtxG003.Provider>
                    :
                    <LoadingBox overlay={false} loadingDone={loadingDone}><></></LoadingBox>
                }
            </ContentsBox>
            {params.mode === G003MODE_MONTHLY ?
                <BtnReturn onClick={function(){setMoveflg(true);history.goBack()}}/>
                :
                <BtnReturn onClick={function(){setCDate(cMonth);history.goBack()}} />
            }
        </>
    );

}